import { FormikValues } from 'formik';

import { radioYes } from '@utils/options';
import { Profile, User } from '@/types/user';
import { parseDateQuery, parseDateQueryChild } from '@utils/date';

const defaultProfile = {
  id: null,
  gender: null,
  birthDate: null,
  identifier: null,
  address: null,
  children: null,
  pets: null,
  partner: null,
  numberOfHomeComponents: null,
  educationLevel: null,
  allergies: null,
  diet: null,
  interests: null,
  lifeStyles: null,
  preference: null,
  communicationsPolicy: null,
};

const parseBirthDateFormatToMutation = (date: string): string | null => {
  if (!date) {
    return null;
  }
  const split = date.split('-');

  return split[0].length === 4 ? `${split[0]}-${split[1]}-${split[2]}` : `${split[2]}-${split[1]}-${split[0]}`;
};

const sanitizePhone = (phone?: string) => {
  return phone?.replaceAll(/\s/g, '').replaceAll('+', '').replaceAll('(', '').replaceAll(')', '');
};
const buildAllergens = (hasAllergens: string, allergens: string[] | null) => {
  const output: any = [];

  if (hasAllergens === 'NO') {
    return output;
  }

  allergens?.map((allergen) => output.push({ code: allergen }));
  return output;
};

const buildEducation = (code: string) => {
  return {
    code,
    name: '',
    description: '',
  };
};
const buildLifeStyle = (codes: string[]) => {
  const output: any[] = [];

  codes?.forEach((item: string) => {
    const style = { code: item };
    output.push({
      ...style,
    });
  });

  return output;
};
const buildPets = (petsValues: string[]) => {
  const output: any[] = [];

  petsValues?.forEach((item: string) => {
    const pet = { type: item };
    output.push({
      ...pet,
    });
  });

  return output;
};
const buildUpdateChilds = (values: any, recordContext: { children: string | any[] }) => {
  const { hasChild, ...rest } = values;
  const beforeChildren = recordContext?.children || [];
  const parsedHasChild = parseInt(hasChild, 10);
  const childDates = Array.from({ length: 10 }, (_, i) => rest[`childDate_${i + 1}`]);

  if (beforeChildren.length === parsedHasChild) {
    const updates = childDates
      .map((date, index) => {
        if (!date) return null;
        const parsedDate = parseDateQueryChild(date);
        return parsedDate ? { index, input: { birthDate: parsedDate } } : null;
      })
      .filter(Boolean);

    return updates.length ? { update: updates } : null;
  }

  if (beforeChildren.length > parsedHasChild) {
    const deletedIndices = [];
    for (let i = parsedHasChild; i < beforeChildren.length; i += 1) {
      deletedIndices.push(i);
    }
    return { delete: deletedIndices };
  }

  if (beforeChildren.length < parsedHasChild) {
    const toCreateCount = parsedHasChild - beforeChildren.length;
    const newDates = childDates.slice(beforeChildren.length, beforeChildren.length + toCreateCount);

    const creates = newDates
      .map((date) => {
        const parsedDate = parseDateQueryChild(date);
        return parsedDate ? { birthDate: parsedDate } : null;
      })
      .filter(Boolean);

    return creates.length ? { create: creates } : null;
  }

  return null;
};

const buildPreference = (preferenceValues: string[], preference_alcohol: boolean | undefined) => {
  const alcohol = preference_alcohol !== undefined ? preference_alcohol : null;
  const bio = preferenceValues?.indexOf('bio') !== -1;
  const light = preferenceValues?.indexOf('light') !== -1;
  const noGluten = preferenceValues?.indexOf('noGluten') !== -1;

  return {
    alcohol,
    bio,
    light,
    noGluten,
  };
};
const buildPreferencesFromQuery = (values: { alcohol: boolean; bio: boolean; light: boolean; noGluten: boolean }) => {
  const output = [];
  if (values?.alcohol === true) {
    output.push('alcohol');
  }
  if (values?.alcohol === false) {
    output.push('NonAlcohol');
  }
  if (values?.alcohol === null) {
    output.push('NullAlcohol');
  }
  if (values?.bio === true) {
    output.push('bio');
  }
  if (values?.light === true) {
    output.push('light');
  }
  if (values?.noGluten === true) {
    output.push('noGluten');
  }

  return output;
};
const buildCodesFromQuery = (values: [{ code: string }]) => {
  const output: string[] = [];

  values?.forEach((item: { code: string }) => {
    output.push(item?.code);
  });

  return output;
};
const isTelephoneVerified = (verified: string | undefined | null) => {
  return !!verified;
};

const buildDataUserFromProfile = (props: any) => {
  const { me, profile } = props;
  const { username, name, lastName, imageUrl, communicationsPolicy, isTelephoneConfirmed, telephone, id: userId } = me;
  const {
    gender,
    birthDate,
    address,
    allergies,
    children,
    pets,
    educationLevel,
    partner,
    numberOfHomeComponents,
    interests,
    diet,
    lifestyles,
    preference,
    id,
    identifier,
  } = profile || defaultProfile;
  const {
    line1,
    line2,
    city,
    postalCode,
    state,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
  } = address || {};
  const firstName = partner?.firstName;
  const birthDatePartner = partner?.birthDate;
  const genderPartner = partner?.gender;
  const hasPartner = birthDatePartner ? 'YES' : 'NO';
  const hasPet = pets && pets?.length > 0 ? 'YES' : 'NO';
  const hasAllergies = () => {
    if (allergies === null) {
      return undefined;
    }

    return allergies && allergies?.length > 0 ? 'YES' : 'NO';
  };

  return {
    email: username,
    firstName: name,
    lastName,
    photo: imageUrl,
    communicationsPolicy,
    state,
    city,
    zipCode: postalCode,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    line1,
    line2,
    gender,
    birthDate,
    partnerBirthDate: birthDatePartner,
    partnerFirstName: firstName,
    partnerGender: genderPartner,
    educationLevelCode: educationLevel?.code,
    lifeStyleCode: buildCodesFromQuery(lifestyles),
    dietCode: diet?.code,
    interestCode: buildCodesFromQuery(interests),
    partner: {
      firstName,
      birthDate: birthDatePartner,
      gender: genderPartner,
    },
    hasPet,
    hasPartner,
    hasAllergies: hasAllergies(),
    children,
    identifier,
    pets: pets ? pets?.map((object: any) => object.type) : null,
    numberOfHomeComponents,
    preferences: buildPreferencesFromQuery(preference),
    allergies: buildCodesFromQuery(allergies),
    allergens: [],
    profileId: id,
    userId,
    telephone,
    isTelephoneConfirmed,
    isTelephoneVerified: isTelephoneConfirmed,
  };
};
const buildAlcoholFormValue = (preferenceList: string[] | null) => {
  if (preferenceList && preferenceList.length > 0) {
    if (preferenceList?.indexOf('alcohol') !== -1) {
      return true;
    }
    if (preferenceList?.indexOf('NonAlcohol') !== -1) {
      return false;
    }
  }
  return null;
};
const buildFormValues = (params: any) => {
  const { user } = params;
  const {
    state,
    line1,
    line2,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    zipCode,
    birthDate,
    country,
    city,
    gender,
    hasPartner,
    hasPet,
    hasAllergies,
    pets,
    preferences,
    children,
    partnerBirthDate,
    partnerFirstName,
    partnerGender,
    educationLevelCode,
    lifeStyleCode,
    dietCode,
    interestCode,
    partner,
    numberOfHomeComponents,
    allergies,
    profileId,
    telephone,
    telephoneVerifiedAt,
    identifier,
    communicationsPolicy,
  } = user;
  const parsed = parseDateQuery(birthDate, 'DD/MM/YYYY');
  const children2Processed = children || [];
  const childrenParsed = children2Processed
    ?.map((i: { birthDate: string | null }) => ({
      birthDate: parseDateQuery(i.birthDate, 'DD/MM/YYYY'),
    }))
    .filter((c: { birthDate: any }) => c.birthDate !== '');
  const output: Profile = {
    state,
    line1,
    line2,
    zipCode,
    birthDate: parsed ?? undefined,
    country,
    city,
    gender,
    hasPartner,
    hasPet: pets === null ? undefined : hasPet,
    pets,
    preferences,
    children: childrenParsed,
    partnerBirthDate,
    partnerFirstName,
    partnerGender,
    educationLevelCode,
    lifeStyleCode,
    dietCode,
    interestCode,
    partner,
    numberOfHomeComponents,
    hasAllergies: allergies === null ? undefined : hasAllergies,
    allergies,
    telephone,
    profileId,
    isTelephoneConfirmed: telephoneVerifiedAt,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    identifier,
    communicationsPolicy,
  };
  const childrenLength = childrenParsed ? childrenParsed?.length.toString() : '0';
  return {
    ...output,
    name: user.firstName,
    lastName: user.lastName,
    email: user.email,
    preference_alcohol: buildAlcoholFormValue(preferences),
    hasChild: children !== null && childrenLength ? childrenLength : null,
  };
};
const buildRequestParametersPre = (params: any) => {
  const { values, user } = params;
  const {
    line1,
    line2,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    telephone,
    identifier,
  } = values;
  const {
    gender,
    birthDate,
    city,
    children,
    zipCode,
    state,
    numberOfHomeComponents,
    hasPartner,
    pets,
    partnerBirthDate,
    partnerFirstName,
    partnerGender,
    educationLevelCode,
    lifeStyleCode,
    interestCode,
    dietCode,
  } = user;
  const buildValueEducation = buildEducation(educationLevelCode);
  const buildValueLifeStyles = buildLifeStyle(lifeStyleCode);
  const buildValueInterest = buildLifeStyle(interestCode);
  const partnerObject =
    hasPartner === 'YES'
      ? {
          firstName: partnerFirstName,
          birthDate: partnerBirthDate !== '' ? partnerBirthDate : null,
          gender: partnerGender !== '' ? partnerGender : null,
        }
      : null;
  const input = {
    gender,
    birthDate: parseBirthDateFormatToMutation(birthDate),
    numberOfHomeComponents,
    partner: partnerObject,
    lifestyles: buildValueLifeStyles,
    diet: dietCode ? { code: dietCode } : null,
    interests: buildValueInterest,
    pets: pets ? pets?.map((p: any) => ({ type: p })) : null,
    educationLevel: buildValueEducation,
    address: {
      city,
      line1,
      line2,
      state,
      postalCode: zipCode,
      administrativeAreaLevel1,
      administrativeAreaLevel2,
      administrativeAreaLevel3,
    },
    children: children
      ? children?.map((c: { firstName: any; birthDate: any }) => ({
          firstName: c.firstName,
          birthDate: parseBirthDateFormatToMutation(c.birthDate),
        }))
      : null,
    preference: { alcohol: null },
    phoneNumber: sanitizePhone(telephone),
    identifier,
  };
  return { input };
};
const buildRequestParameters = (params: any) => {
  const { values, user } = params;
  const {
    gender,
    birthDate,
    city,
    line1,
    line2,
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    zipCode,
    state,
    numberOfHomeComponents,
    hasPartner,
    pets,
    hasChild,
    preferences,
    partnerBirthDate,
    partnerFirstName,
    partnerGender,
    educationLevelCode,
    lifeStyleCode,
    interestCode,
    dietCode,
    telephone,
    preference_alcohol,
    identifier,
    hasAllergies,
    allergies,
    communicationsPolicy,
  } = values;
  const { userId } = user;
  const zero = '0';
  const buildValuePets = buildPets(pets);
  const buildValueChilds = buildUpdateChilds(values, user);
  const buildValuePreference = buildPreference(preferences, preference_alcohol);
  const buildValueEducation = buildEducation(educationLevelCode);
  const buildValueAllergies = buildAllergens(hasAllergies, allergies);
  const buildValueLifeStyles = buildLifeStyle(lifeStyleCode);
  const buildValueInterest = buildLifeStyle(interestCode);
  const partnerObject =
    hasPartner === 'YES'
      ? {
          firstName: partnerFirstName,
          birthDate: partnerBirthDate !== '' ? partnerBirthDate : null,
          gender: partnerGender !== '' ? partnerGender : null,
        }
      : null;
  const input = {
    gender,
    birthDate: parseDateQueryChild(birthDate),
    numberOfHomeComponents,
    partner: partnerObject,
    lifestyles: buildValueLifeStyles,
    diet: dietCode ? { code: dietCode } : null,
    interests: buildValueInterest,
    pets: buildValuePets,
    educationLevel: buildValueEducation,
    address: {
      city,
      line1,
      line2,
      state,
      postalCode: zipCode,
      administrativeAreaLevel1,
      administrativeAreaLevel2,
      administrativeAreaLevel3,
    },
    ...(buildValueChilds ? { updateChildrenInput: buildValueChilds } : {}),
    ...(!buildValueChilds && (numberOfHomeComponents === '1' || hasChild === zero) ? { children: [] } : {}),
    preference: buildValuePreference,
    allergies: buildValueAllergies,
    phoneNumber: sanitizePhone(telephone),
    identifier,
  };

  const userInput = {
    communicationsPolicy,
  };

  return { input, userId, userInput };
};
const buildRequestAddressParameters = (params: any) => {
  const { values } = params;
  const { line1, line2, administrativeAreaLevel1, administrativeAreaLevel2, administrativeAreaLevel3 } = values;

  const input = {
    address: {
      line1,
      line2,
      administrativeAreaLevel1,
      administrativeAreaLevel2,
      administrativeAreaLevel3,
    },
  };
  return { input };
};
const buildRequest2StepsParameters = (params: any) => {
  const { values, user } = params;
  const { numberOfHomeComponents, pets, hasAllergies, allergies, dietCode, preference_alcohol, hasChild } = values;

  const buildValuePets = buildPets(pets);
  const buildValueChilds = buildUpdateChilds(values, user);
  const isAcoholLike = preference_alcohol === radioYes.value;
  const buildValuePreference = buildPreference(user.preferences, isAcoholLike);
  const buildValueEducation = buildEducation(user.educationLevelCode);
  const buildValueAllergies = buildAllergens(hasAllergies, allergies);
  const partnerObject = null;
  const input = {
    gender: user.gender,
    birthDate: parseBirthDateFormatToMutation(user.birthDate),
    numberOfHomeComponents,
    partner: partnerObject,
    allergies: buildValueAllergies,
    diet: dietCode ? { code: dietCode } : null,
    pets: buildValuePets,
    educationLevel: buildValueEducation,
    address: {
      city: user?.city,
      line1: user?.line1,
      line2: user?.line2,
      state: user?.state,
      postalCode: user?.zipCode,
      administrativeAreaLevel1: user?.administrativeAreaLevel1,
      administrativeAreaLevel2: user?.administrativeAreaLevel2,
      administrativeAreaLevel3: user?.administrativeAreaLevel3,
    },
    ...(hasChild !== '0' && buildValueChilds ? { updateChildrenInput: buildValueChilds } : { children: [] }),
    preference: buildValuePreference,
    phoneNumber: sanitizePhone(user?.telephone),
    identifier: user?.identifier,
  };
  return { input };
};

const buildProfileParameters = (props: FormikValues) => {
  const parseBirthDate = (date: string) => {
    const splitDate = date.split('/');

    return `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;
  };

  const { gender, birthDate, state, city, zipCode, line1, line2 } = props;
  const input = {
    gender,
    birthDate: parseBirthDate(birthDate),
    address: {
      line1: line1 === '' ? null : line1,
      line2: line2 === '' ? null : line2,
      postalCode: zipCode === '' ? null : zipCode,
      city: city === '' ? null : city,
      state: state === '' ? null : state,
    },
  };
  return { input };
};

const isProfileCompleted = (user: User) => {
  const { hasPet, dietCode, numberOfHomeComponents, preferences, hasAllergies, children } = user;
  const isNotCompleted =
    hasPet === undefined ||
    children === null ||
    children === undefined ||
    numberOfHomeComponents === undefined ||
    dietCode === undefined ||
    preferences.includes('NullAlcohol') ||
    hasAllergies === undefined;

  return !isNotCompleted;
};
const isProfileCompletedPre = (user: User) => {
  const {
    administrativeAreaLevel1,
    administrativeAreaLevel3,
    administrativeAreaLevel2,
    line1,
    telephone,
    isTelephoneConfirmed,
    identifier,
  } = user;
  const isNotCompleted =
    !line1 ||
    !administrativeAreaLevel1 ||
    !administrativeAreaLevel2 ||
    !administrativeAreaLevel3 ||
    !telephone ||
    !identifier ||
    !isTelephoneConfirmed;

  return !isNotCompleted;
};

const ID_ALREADY_EXISTS = 'A profile with identifier';
const PHONE_ALREADY_EXISTS = 'One or more users already have the following properties: telephone';
const idExists = 'Este número de cédula ya existe';
const phoneExists = 'Este número de teléfono ya existe';

enum stepsProfile {
  STEP_1 = 'STEP_1',
  STEP_2 = 'STEP_2',
}
export {
  stepsProfile,
  buildFormValues,
  buildRequestParameters,
  buildProfileParameters,
  buildDataUserFromProfile,
  isProfileCompleted,
  buildRequestParametersPre,
  isProfileCompletedPre,
  isTelephoneVerified,
  sanitizePhone,
  buildRequest2StepsParameters,
  buildRequestAddressParameters,
  ID_ALREADY_EXISTS,
  PHONE_ALREADY_EXISTS,
  idExists,
  phoneExists,
};
